import type { ApprovalGatedResult } from '@risksmart-app/trpc/src/helpers/with-approval-workflow';
import { mapScheduleToTRPC } from '@risksmart-app/trpc/src/types';
import type { UpdateControlMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateControlDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { unwrapApprovalGated } from '@/hooks/mutations/unwrapApprovalGated';
import { createMutationHook } from '@/utils/hook-factories';

import type { FrontendRouterInputs } from '../routerInputs';
import type { UpdateControlInput } from '../scheduledEntityInputs';

type UpdateControlTRPCInput = FrontendRouterInputs['control']['update'];

/**
 * Maps the tRPC mutation response to the GraphQL mutation structure. The applied
 * entity is discarded — the legacy shape reports a fixed affected-row count.
 */
const mapTrpcResponseToGraphQL = (_applied: {
  Id: string;
}): UpdateControlMutation => ({
  updateChildControl: {
    __typename: 'GenericMutationOutput',
    affected_rows: 1,
  },
});

export const useUpdateControl = createMutationHook({
  name: 'updateControl',
  isChangeRequestGated: true,
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.control.update.mutationOptions({ onSuccess }),
  mapInput: ({
    schedule,
    ...rest
  }: UpdateControlInput): UpdateControlTRPCInput => ({
    ...rest,
    ...mapScheduleToTRPC(schedule),
  }),
  // RSP-5789 — when the mutation was gated into a change request (created or
  // amended), surface it on the CR read feeds immediately.
  refreshChangeRequestFeedsWhen: (
    result: ApprovalGatedResult<{ Id: string }>
  ) => result.status !== 'applied',
  invalidate: (trpc) => [
    trpc.frontend.control.register.queryKey(),
    trpc.frontend.control.controlById.queryKey(),
    trpc.frontend.control.controlsByUserId.queryKey(),
    trpc.frontend.control.controlsBasic.queryKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
  ],
  // A confirmed update resolves as `applied` OR raises a CR — both are success.
  // Echo the input Id on the CR branch (the entity is unchanged).
  mapResponse: (
    result: ApprovalGatedResult<{ Id: string }>,
    input: UpdateControlInput
  ) => mapTrpcResponseToGraphQL(unwrapApprovalGated(result, { Id: input.Id })),
  graphqlDocument: UpdateControlDocument,
  graphqlVariables: (input: UpdateControlInput) => ({ object: input }),
  evictFields: ['control'],
  refetchQueries: [namedOperations.Query.getControlById],
});
